/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListSkillVersionsRequest,
  ListSkillVersionsSecurity,
} from "../models/operations/listskillversions.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildSkillVersionsInfiniteQuery,
  buildSkillVersionsQuery,
  prefetchSkillVersions,
  prefetchSkillVersionsInfinite,
  queryKeySkillVersions,
  queryKeySkillVersionsInfinite,
  SkillVersionsInfiniteQueryData,
  SkillVersionsPageParams,
  SkillVersionsQueryData,
} from "./skillVersions.core.js";
export {
  buildSkillVersionsInfiniteQuery,
  buildSkillVersionsQuery,
  prefetchSkillVersions,
  prefetchSkillVersionsInfinite,
  queryKeySkillVersions,
  queryKeySkillVersionsInfinite,
  type SkillVersionsInfiniteQueryData,
  type SkillVersionsPageParams,
  type SkillVersionsQueryData,
};

export type SkillVersionsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listVersions skills
 *
 * @remarks
 * List immutable versions of an active skill, newest first. The implementation requires the skills product feature and skill read scope.
 */
export function useSkillVersions(
  request: ListSkillVersionsRequest,
  security?: ListSkillVersionsSecurity | undefined,
  options?: QueryHookOptions<SkillVersionsQueryData, SkillVersionsQueryError>,
): UseQueryResult<SkillVersionsQueryData, SkillVersionsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildSkillVersionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listVersions skills
 *
 * @remarks
 * List immutable versions of an active skill, newest first. The implementation requires the skills product feature and skill read scope.
 */
export function useSkillVersionsSuspense(
  request: ListSkillVersionsRequest,
  security?: ListSkillVersionsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    SkillVersionsQueryData,
    SkillVersionsQueryError
  >,
): UseSuspenseQueryResult<SkillVersionsQueryData, SkillVersionsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildSkillVersionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listVersions skills
 *
 * @remarks
 * List immutable versions of an active skill, newest first. The implementation requires the skills product feature and skill read scope.
 */
export function useSkillVersionsInfinite(
  request: ListSkillVersionsRequest,
  security?: ListSkillVersionsSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    SkillVersionsInfiniteQueryData,
    SkillVersionsQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<SkillVersionsInfiniteQueryData, SkillVersionsPageParams>,
  SkillVersionsQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    SkillVersionsInfiniteQueryData,
    SkillVersionsQueryError,
    InfiniteData<SkillVersionsInfiniteQueryData, SkillVersionsPageParams>,
    QueryKey,
    SkillVersionsPageParams
  >({
    ...buildSkillVersionsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listVersions skills
 *
 * @remarks
 * List immutable versions of an active skill, newest first. The implementation requires the skills product feature and skill read scope.
 */
export function useSkillVersionsInfiniteSuspense(
  request: ListSkillVersionsRequest,
  security?: ListSkillVersionsSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    SkillVersionsInfiniteQueryData,
    SkillVersionsQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<SkillVersionsInfiniteQueryData, SkillVersionsPageParams>,
  SkillVersionsQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    SkillVersionsInfiniteQueryData,
    SkillVersionsQueryError,
    InfiniteData<SkillVersionsInfiniteQueryData, SkillVersionsPageParams>,
    QueryKey,
    SkillVersionsPageParams
  >({
    ...buildSkillVersionsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setSkillVersionsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      id: string;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: SkillVersionsQueryData,
): SkillVersionsQueryData | undefined {
  const key = queryKeySkillVersions(...queryKeyBase);

  return client.setQueryData<SkillVersionsQueryData>(key, data);
}

export function invalidateSkillVersions(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      id: string;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "skills", "listVersions", ...queryKeyBase],
  });
}

export function invalidateAllSkillVersions(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "skills", "listVersions"],
  });
}
